import React, { useState, useEffect } from 'react';

const ServiceSections = () => {
  const [activeService, setActiveService] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const services = [
    {
      id: 'corporate-services',
      title: 'Corporate Services',
      subtitle: 'Everything you need to start and run your business.',
      description: 'VentureCorp provides founders with everything they need to run their business, including accounting, registered agent services, a premium physical US address, and tax filing services.',
      features: ['Business Registration', 'Legal Compliance', 'Corporate Governance', 'International Setup'],
      reportTitle: 'Corporate Dashboard',
      reportType: 'dashboard',
      mockupData: {
        type: 'dashboard',
        mainCard: {
          title: 'Total tax refund',
          amount: '$13,405.23',
          buttonText: 'Process now'
        },
        secondaryCards: [
          {
            type: 'document',
            title: 'You\'ve got mail from',
            subtitle: 'The State of Delaware',
            time: '2mins ago',
            buttonText: 'Open'
          },
          {
            type: 'report',
            label: 'Accounting Report',
            title: 'Investor Report',
            period: 'Q3',
            buttonText: 'Download now',
            hasChart: true
          }
        ],
        notification: 'Foreign qualification in California approved • Just now'
      }
    },
    {
      id: 'hr-management',
      title: 'HR Management Services',
      subtitle: 'Build and manage exceptional teams globally.',
      description: 'Comprehensive HR solutions to recruit, manage, and retain top talent across international markets with full compliance support.',
      features: ['Global Recruitment', 'Employee Relations', 'Performance Management', 'Compliance Support'],
      reportTitle: 'Recruitment Pipeline',
      reportType: 'timeline',
      mockupData: {
        type: 'timeline',
        items: [
          {
            title: 'Senior Developer Position - UK Posted',
            time: '5 days ago',
            status: 'completed',
            hasSubStep: true,
            subStep: '12 applications received - 2 days ago'
          },
          {
            title: 'Initial Screening Completed',
            time: '3 days ago',
            status: 'completed',
            hasSubStep: true,
            subStep: '5 candidates shortlisted - 1 day ago'
          },
          {
            title: 'First Round Interviews',
            time: '1 day ago',
            status: 'active',
            hasSubStep: true,
            subStep: 'Schedule remaining interviews'
          }
        ]
      }
    },
    {
      id: 'accounting-services',
      title: 'Accounting Services',
      subtitle: 'Professional financial management solutions.',
      description: 'Keep your startup financially healthy with our comprehensive accounting services including bookkeeping, tax planning, and financial reporting.',
      features: ['Bookkeeping', 'Tax Planning', 'Financial Reporting', 'Multi-jurisdiction Support'],
      reportTitle: 'Financial Dashboard',
      reportType: 'dashboard',
      mockupData: {
        type: 'dashboard',
        mainCard: {
          title: 'Monthly revenue',
          amount: '$2.4M',
          buttonText: 'View report'
        },
        secondaryCards: [
          {
            type: 'document',
            title: 'Invoice payment from',
            subtitle: 'Client #1247 - $15,000',
            time: '30m ago',
            buttonText: 'Details'
          },
          {
            type: 'report',
            label: 'Financial Report',
            title: 'Q3 Analytics',
            period: 'Profit: +23%',
            buttonText: 'Download now',
            hasChart: true
          }
        ],
        notification: 'Tax filing deadline approaching: 15 days remaining'
      }
    },
    {
      id: 'social-media',
      title: 'Social Media Management',
      subtitle: 'Amplify your brand across global markets.',
      description: 'Strategic social media management to engage customers and build your brand presence across international platforms.',
      features: ['Content Strategy', 'Multi-platform Management', 'Growth Optimization', 'Analytics & Reporting'],
      reportTitle: 'Content Calendar',
      reportType: 'timeline',
      mockupData: {
        type: 'timeline',
        items: [
          {
            title: 'Product Launch Campaign - LinkedIn',
            time: '3 days ago',
            status: 'completed',
            hasSubStep: true,
            subStep: '12K likes, 340 shares - 2 days ago'
          },
          {
            title: 'Weekly Analytics Report',
            time: '1 day ago',
            status: 'completed',
            hasSubStep: true,
            subStep: 'Engagement up 47% this month'
          },
          {
            title: 'Cross-platform Content Push',
            time: '2 hours ago',
            status: 'active',
            hasSubStep: true,
            subStep: 'Schedule remaining posts'
          }
        ]
      }
    },
    {
      id: 'coworking-spaces',
      title: 'Co-working Spaces',
      subtitle: 'Modern workspace solutions for growing teams.',
      description: 'Flexible office solutions and community spaces that foster innovation and create networking opportunities for startups.',
      features: ['Flexible Office Solutions', 'Meeting Rooms', 'Community Events', 'Networking Opportunities'],
      reportTitle: 'Space Management',
      reportType: 'timeline',
      mockupData: {
        type: 'timeline',
        items: [
          {
            title: 'Networking Event Setup Complete',
            time: '2 days ago',
            status: 'completed',
            hasSubStep: true,
            subStep: '45 attendees registered - 1 day ago'
          },
          {
            title: 'Conference Room A - Maintenance',
            time: '4 hours ago',
            status: 'completed',
            hasSubStep: true,
            subStep: 'AV equipment upgraded'
          },
          {
            title: 'Daily Occupancy Report',
            time: '1 hour ago',
            status: 'active',
            hasSubStep: true,
            subStep: 'Current: 78% capacity'
          }
        ]
      }
    }
  ];

  // Handle service change with animation
  const handleServiceChange = (newIndex) => {
    if (newIndex !== activeService) {
      setIsTransitioning(true);
      setProgress(0);
      
      setTimeout(() => {
        setActiveService(newIndex);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 200);
      }, 300);
    }
  };

  // Auto-cycle through services
  useEffect(() => {
    const interval = setInterval(() => {
      handleServiceChange((activeService + 1) % services.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [activeService, services.length]);

  // Progress bar animation
  useEffect(() => {
    if (!isTransitioning) {
      setProgress(0);
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 2; // Increase by 2% every 100ms
        });
      }, 100);

      return () => clearInterval(progressInterval);
    }
  }, [activeService, isTransitioning]);

  const currentService = services[activeService];

  const renderDashboard = () => (
    <div className="bg-gradient-to-br from-purple-600/20 via-indigo-600/20 to-blue-600/20 rounded-2xl p-1">
      <div className="bg-gray-900/90 rounded-2xl p-6 backdrop-blur-sm">
        {/* Top Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Main Tax Refund Card */}
          <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-6">
            <p className="text-white/80 text-sm mb-2">{currentService.mockupData.mainCard.title}</p>
            <h3 className="text-white text-3xl font-bold mb-4">{currentService.mockupData.mainCard.amount}</h3>
            <button className="bg-white/20 text-white px-4 py-2 rounded-lg text-sm hover:bg-white/30 transition-colors">
              {currentService.mockupData.mainCard.buttonText}
            </button>
          </div>

          {/* Investor Report Card */}
          <div className="bg-gray-800 rounded-xl p-6">
            <div className="text-gray-400 text-xs mb-2">{currentService.mockupData.secondaryCards[1].label}</div>
            <h4 className="text-white font-medium mb-2">{currentService.mockupData.secondaryCards[1].title}</h4>
            <div className="text-blue-400 text-sm mb-4">{currentService.mockupData.secondaryCards[1].period}</div>
            
            {/* Chart visualization */}
            <div className="h-24 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-lg mb-4 flex items-end p-3">
              <div className="flex items-end space-x-1 w-full justify-end">
                <div className="w-3 h-8 bg-gradient-to-t from-cyan-400 to-purple-400 rounded-sm"></div>
                <div className="w-3 h-12 bg-gradient-to-t from-cyan-400 to-purple-400 rounded-sm"></div>
                <div className="w-3 h-16 bg-gradient-to-t from-cyan-400 to-purple-400 rounded-sm"></div>
                <div className="w-3 h-20 bg-gradient-to-t from-cyan-400 to-purple-400 rounded-sm"></div>
              </div>
            </div>
            
            <button className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition-colors">
              {currentService.mockupData.secondaryCards[1].buttonText}
            </button>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Document Card */}
          <div className="bg-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-16 bg-white rounded shadow-sm flex items-center justify-center">
                <div className="w-8 h-10 bg-gray-200 rounded text-xs flex items-center justify-center">
                  📄
                </div>
              </div>
              <span className="text-xs text-gray-400">{currentService.mockupData.secondaryCards[0].time}</span>
            </div>
            <p className="text-gray-300 text-sm mb-2">{currentService.mockupData.secondaryCards[0].title}</p>
            <p className="text-white font-medium mb-4">{currentService.mockupData.secondaryCards[0].subtitle}</p>
            <button className="bg-gray-700 text-white px-4 py-2 rounded text-sm hover:bg-gray-600 transition-colors">
              {currentService.mockupData.secondaryCards[0].buttonText}
            </button>
          </div>

          {/* Notification Card */}
          <div className="bg-blue-600/20 border border-blue-500/30 rounded-xl p-6 flex items-center">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-4">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-blue-300 text-sm">{currentService.mockupData.notification}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTimeline = () => (
    <div className="bg-gradient-to-br from-gray-900/95 via-gray-800/90 to-gray-900/95 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8">
      <div className="space-y-6">
        {currentService.mockupData.items.map((item, index) => (
          <div key={index} className="relative">
            {/* Timeline line */}
            {index < currentService.mockupData.items.length - 1 && (
              <div className="absolute left-3 top-8 w-0.5 h-16 bg-gray-600"></div>
            )}
            
            <div className="flex items-start space-x-4">
              {/* Status indicator */}
              <div className="relative flex-shrink-0">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  item.status === 'completed' 
                    ? 'bg-green-500 border-green-500' 
                    : item.status === 'active'
                    ? 'bg-blue-500 border-blue-500'
                    : 'bg-gray-600 border-gray-600'
                }`}>
                  {item.status === 'completed' && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-AfacadFluxLight font-medium">{item.title}</h4>
                  <span className="text-sm font-AfacadFluxLight text-gray-400">{item.time}</span>
                </div>
                
                {item.hasSubStep && (
                  <div className="mt-3 ml-4">
                    <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700/50">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-300">{item.subStep}</span>
                        {item.status === 'completed' && (
                          <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                        {item.status === 'active' && (
                          <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer Stats */}
      <div className="mt-8 pt-6 border-t border-gray-700/50">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">
            {currentService.mockupData.items.filter(item => item.status === 'completed').length} of {currentService.mockupData.items.length} completed
          </span>
          <span className="text-green-400">
            {Math.round((currentService.mockupData.items.filter(item => item.status === 'completed').length / currentService.mockupData.items.length) * 100)}% progress
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <section className="py-32 min-h-screen relative overflow-hidden" style={{ backgroundColor: '#0a0f1c' }}>
      {/* Left corner glow effect during transitions */}
      <div className={`absolute top-0 left-0 w-32 h-32 transition-all duration-600 ease-out ${
        isTransitioning 
          ? 'opacity-30 bg-gradient-radial from-white/40 via-white/20 to-transparent' 
          : 'opacity-0'
      }`} 
      style={{
        background: isTransitioning 
          ? 'radial-gradient(circle at center, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.2) 30%, rgba(255,255,255,0.1) 60%, transparent 100%)'
          : 'transparent'
      }}></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Label and Heading - Animated */}
        <div className="mb-8">
          <div className="mb-6">
            <span className="text-sm font-semibold tracking-widest uppercase text-purple-300">
              Start and Grow
            </span>
          </div>
          <div className={`transition-all duration-600 ease-out ${
            isTransitioning ? 'opacity-0 transform translate-y-2' : 'opacity-100 transform translate-y-0'
          }`}>
            <h2 className="text-4xl md:text-5xl font-AfacadFluxLight lg:text-6xl font-light text-white leading-tight mb-6">
              {currentService.subtitle}
            </h2>
            
            {/* Service Description - Animated */}
            <p className="text-lg text-gray-300 font-AfacadFluxLight leading-relaxed max-w-3xl">
              {currentService.description}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Content - Navigation Only */}
          <div className="lg:col-span-4">
            {/* Single Progress Bar */}
            <div className="mb-8">
              <div className="w-full max-w-xs h-1 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-400 to-indigo-400 transition-all duration-100 ease-linear"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Service Navigation */}
            <div className="space-y-2 mb-8">
              {services.map((service, index) => (
                <div key={service.id} className="relative">
                  <button
                    onClick={() => handleServiceChange(index)}
                    className="w-full text-left p-4 transition-all duration-300 hover:opacity-80"
                  >
                    <div className="flex items-center font-AfacadFluxLight justify-between">
                      <div>
                        <h3 className={`font-medium  transition-colors duration-300 ${
                          index === activeService ? 'text-white border-b border-white pb-1' : 'text-gray-400'
                        }`}>
                          {service.title}
                        </h3>
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Right Report Interface - Animated */}
          <div className="lg:col-span-8">
            <div className="relative min-h-[600px]">
              {/* Report Title - Animated */}
              <div className={`mb-4 transition-all duration-600 ease-out delay-100 ${
                isTransitioning ? 'opacity-0 transform translate-x-4' : 'opacity-100 transform translate-x-0'
              }`}>
                {/* <h3 className="text-xl font-light text-white/70 mb-2">{currentService.reportTitle}</h3> */}
              </div>

              {/* Report Content with Nice Fade Effect - Animated */}
              <div className={`relative opacity-70 transform scale-95 transition-all duration-700 ease-out delay-150 min-h-[500px] ${
                isTransitioning ? 'opacity-0 transform scale-92 translate-x-6' : 'opacity-70 transform scale-95 translate-x-0'
              }`}>
                {/* Lighter background blend overlay */}
                <div className="absolute inset-0 bg-[#0a0f1c]/30 rounded-2xl pointer-events-none z-10"></div>
                
                {currentService.reportType === 'dashboard' ? (
                  <div className="bg-gradient-to-br from-purple-600/15 via-indigo-600/15 to-blue-600/15 rounded-2xl p-1">
                    <div className="bg-gray-900/60 rounded-2xl p-5 backdrop-blur-sm">
                      {/* Top Row */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                        {/* Main Tax Refund Card */}
                        <div className="bg-gradient-to-r from-purple-500/80 to-indigo-600/80 rounded-xl p-5">
                          <p className="text-white/70 text-sm mb-2">{currentService.mockupData.mainCard.title}</p>
                          <h3 className="text-white text-2xl font-bold mb-3">{currentService.mockupData.mainCard.amount}</h3>
                          <button className="bg-white/20 text-white px-4 py-2 rounded-lg text-sm hover:bg-white/30 transition-colors">
                            {currentService.mockupData.mainCard.buttonText}
                          </button>
                        </div>

                        {/* Investor Report Card */}
                        <div className="bg-gray-800/80 rounded-xl p-5">
                          <div className="text-gray-400 text-xs mb-2">{currentService.mockupData.secondaryCards[1].label}</div>
                          <h4 className="text-white font-medium mb-2">{currentService.mockupData.secondaryCards[1].title}</h4>
                          <div className="text-blue-400 text-sm mb-3">{currentService.mockupData.secondaryCards[1].period}</div>
                          
                          {/* Chart visualization */}
                          <div className="h-20 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-lg mb-4 flex items-end p-3">
                            <div className="flex items-end space-x-1 w-full justify-end">
                              <div className="w-2 h-6 bg-gradient-to-t from-cyan-400/80 to-purple-400/80 rounded-sm"></div>
                              <div className="w-2 h-8 bg-gradient-to-t from-cyan-400/80 to-purple-400/80 rounded-sm"></div>
                              <div className="w-2 h-10 bg-gradient-to-t from-cyan-400/80 to-purple-400/80 rounded-sm"></div>
                              <div className="w-2 h-12 bg-gradient-to-t from-cyan-400/80 to-purple-400/80 rounded-sm"></div>
                            </div>
                          </div>
                          
                          <button className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition-colors">
                            {currentService.mockupData.secondaryCards[1].buttonText}
                          </button>
                        </div>
                      </div>

                      {/* Bottom Row */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Document Card */}
                        <div className="bg-gray-800/80 rounded-xl p-5">
                          <div className="flex items-center justify-between mb-4">
                            <div className="w-10 h-12 bg-white/30 rounded shadow-sm flex items-center justify-center">
                              <div className="w-8 h-10 bg-gray-200/60 rounded text-xs flex items-center justify-center">
                                📄
                              </div>
                            </div>
                            <span className="text-xs text-gray-400">{currentService.mockupData.secondaryCards[0].time}</span>
                          </div>
                          <p className="text-gray-300 text-sm mb-2">{currentService.mockupData.secondaryCards[0].title}</p>
                          <p className="text-white font-medium mb-4">{currentService.mockupData.secondaryCards[0].subtitle}</p>
                          <button className="bg-gray-700 text-white px-4 py-2 rounded text-sm hover:bg-gray-600 transition-colors">
                            {currentService.mockupData.secondaryCards[0].buttonText}
                          </button>
                        </div>

                        {/* Notification Card */}
                        <div className="bg-blue-600/15 border border-blue-500/30 rounded-xl p-5 flex items-center">
                          <div className="w-8 h-8 bg-blue-600/80 rounded-full flex items-center justify-center mr-4">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-blue-300 text-sm">{currentService.mockupData.notification}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-gray-900/80 via-gray-800/70 to-gray-900/80 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6">
                    <div className="space-y-5">
                      {currentService.mockupData.items.map((item, index) => (
                        <div key={index} className="relative">
                          {/* Timeline line */}
                          {index < currentService.mockupData.items.length - 1 && (
                            <div className="absolute left-3 top-7 w-0.5 h-14 bg-gray-600/70"></div>
                          )}
                          
                          <div className="flex items-start space-x-4">
                            {/* Status indicator */}
                            <div className="relative flex-shrink-0">
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                item.status === 'completed' 
                                  ? 'bg-green-500 border-green-500' 
                                  : item.status === 'active'
                                  ? 'bg-blue-500 border-blue-500'
                                  : 'bg-gray-600 border-gray-600'
                              }`}>
                                {item.status === 'completed' && (
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 AfacadFluxLight  min-w-0">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-white  font-medium">{item.title}</h4>
                                <span className="text-sm text-gray-400">{item.time}</span>
                              </div>
                              
                              {item.hasSubStep && (
                                <div className="mt-3 ml-4">
                                  <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700/50">
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-gray-300">{item.subStep}</span>
                                      {item.status === 'completed' && (
                                        <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                      {item.status === 'active' && (
                                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Footer Stats */}
                    <div className="mt-8 pt-6 border-t border-gray-700/50">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">
                          {currentService.mockupData.items.filter(item => item.status === 'completed').length} of {currentService.mockupData.items.length} completed
                        </span>
                        <span className="text-green-400">
                          {Math.round((currentService.mockupData.items.filter(item => item.status === 'completed').length / currentService.mockupData.items.length) * 100)}% progress
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Subtle edge fade overlays */}
                <div className="absolute top-0 right-0 w-20 h-full bg-gradient-to-l from-[#0a0f1c] via-[#0a0f1c]/20 to-transparent pointer-events-none rounded-r-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceSections; 